<template>
  <div class="dictionaryItem-list">
    <!-- 子页面操作栏上方新增父级信息区域 -->
    <el-card class="parent-info-card" shadow="hover">
      <!-- 面包屑导航 -->
      <el-breadcrumb separator-class="el-icon-arrow-right" style="margin-bottom: 10px;">
        <el-breadcrumb-item :to="{ name: 'DictionaryList' }">字典类型列表</el-breadcrumb-item>
        <el-breadcrumb-item>{{ parentDictTypeName }}</el-breadcrumb-item>
        <el-breadcrumb-item>字典项列表</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="dict-type-info">
        <div class="info-row">
          <span class="info-label">关联字典类型：</span>
          <span class="info-value">{{ parentDictTypeName }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">类型编码：</span>
          <span class="info-value">{{ parentDictTypeCode }}</span>
        </div>
      </div>
    </el-card>
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增字典项按钮 -->
        <el-button icon="el-icon-plus" type="primary" @click="openAddDialog"
          v-hasPermission="'dict:edit'">新增字典项</el-button>
        <!-- 批量删除按钮（选中字典项后可用） -->
        <el-button icon="el-icon-delete" type="danger" v-hasPermission="'dict:edit'" @click="handleBatchDelete"
          :disabled="!this.hasPermission('dict:edit') || selectedIds.length === 0">批量删除</el-button>
        <!-- 返回父页面按钮 -->
        <el-button icon="el-icon-back" @click="$router.back()">
          返回字典类型列表</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="字典项键">
            <el-input v-model="searchForm.dictItemKey" placeholder="请输入字典项键" maxlength="20"></el-input>
          </el-form-item>
          <el-form-item label="字典项值">
            <el-input v-model="searchForm.dictItemValue" placeholder="请输入字典项值" maxlength="20"></el-input>
          </el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="loadDictionaryItems">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table :data="dictionaryItems" border stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center"></el-table-column>
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="dictItemKey" label="字典项键" align="center"></el-table-column>
      <el-table-column prop="dictItemValue" label="字典项值" align="center"></el-table-column>
      <el-table-column prop="orderNum" label="显示顺序" align="center"></el-table-column>
      <el-table-column prop="createTime" label="创建时间"
        :formatter="(row, column, cellValue) => $options.filters.formatTime(cellValue)"
        align="center"></el-table-column>
      <el-table-column label="操作" align="center">
        <template slot-scope="scope">
          <el-tooltip content="编辑" placement="top">
            <el-button icon="el-icon-edit" type="primary" circle v-hasPermission="'dict:edit'"
              @click="openEditDialog(scope.row)"></el-button>
          </el-tooltip>
          <el-tooltip content="删除" placement="top">
            <el-button icon="el-icon-delete" type="danger" circle v-hasPermission="'dict:edit'"
              @click="handleDelete(scope.row.id)"></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
      :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total"></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="150px">
        <el-form-item label="字典项键" prop="dictItemKey">
          <el-input v-model="form.dictItemKey" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="字典项值" prop="dictItemValue">
          <el-input v-model="form.dictItemValue" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="显示顺序" prop="orderNum">
          <el-input-number v-model="form.orderNum" controls-position="right" :min="0" :max="99"></el-input-number>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import dictApi from '../../api/dictionary'
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'DictionaryItemList',
  data() {
    return {
      // 表格数据
      dictionaryItems: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      dictTypeId: null, // 父级字典类型ID

      parentDictTypeName: '', // 父级字典类型名称
      parentDictTypeCode: '', // 父级字典类型编码

      // 搜索表单
      searchForm: {
        dictItemKey: '',
        dictItemValue: ''
      },

      // 选中的行
      selectedIds: [],

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增字典项',
      isEdit: false,
      form: {
        id: null,
        dictTypeId: this.dictTypeId,
        dictItemKey: '',
        dictItemValue: '',
        orderNum: 0,
        status: 1
      },
      rules: {
        dictItemKey: [
          { required: true, message: '请输入字典项键', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9]+$/, message: '字典项键只能包含字母和数字', trigger: 'blur' },
          { min: 2, max: 20, message: '字典项键长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        dictItemValue: [
          { required: true, message: '请输入字典项值', trigger: 'blur' },
          { min: 1, max: 20, message: '字典项值长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        orderNum: [
          { required: true, message: '请输入显示顺序', trigger: 'blur' },
          { type: 'number', min: 0, max: 99, message: '请输入在 0 到 99 之间的数字', trigger: 'blur' }
        ]
      }
    }
  },

  mounted() {
    this.dictTypeId = this.$route.query.dictTypeId;
    this.loadParentDictTypeInfo(this.dictTypeId); // 加载父级字典类型信息
    this.loadDictionaryItems()
  },

  methods: {
    // 加载字典项列表
    loadDictionaryItems() {
      // 组装请求参数
      const params = {
        dictItemKey: this.searchForm.dictItemKey, // 搜索-字典项键
        dictItemValue: this.searchForm.dictItemValue,       // 搜索-字典项值
        dictTypeId: this.dictTypeId,
        page: this.currentPage,           // 分页-当前页
        size: this.pageSize               // 分页-每页条数
      };
      dictApi.findAllDictItem(params).then(response => {
        if (response.success) {
          this.dictionaryItems = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载字典项失败：' + error.message)
      })
    },

    // 加载父级字典类型信息（用于面包屑和上下文展示）
    loadParentDictTypeInfo(dictTypeId) {
      dictApi.findDictTypeById(dictTypeId).then(res => {
        if (res.success) {
          this.parentDictTypeName = res.data.dictTypeName;
          this.parentDictTypeCode = res.data.dictTypeCode;
        }
      });
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        dictItemKey: '',
        dictItemValue: ''
      }
      this.loadDictionaryItems()
    },

    // 每页条数变化时
    handleSizeChange(val) {
      this.pageSize = val;
      this.loadDictionaryItems(); // 重新加载
    },

    // 当前页变化时
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadDictionaryItems(); // 重新加载
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 打开新增对话框
    openAddDialog() {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无新增字典项权限');
        return;
      }
      this.dialogTitle = '新增字典项'
      this.isEdit = false
      this.form = {
        id: null,
        dictTypeId: this.dictTypeId,
        dictItemKey: '',
        dictItemValue: '',
        orderNum: 0,
        status: 1
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无编辑字典项权限');
        return;
      }
      this.dialogTitle = '编辑字典项'
      this.isEdit = true
      this.form = { ...row }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 更新字典项
            dictApi.updateDictItem(this.form.id, this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadDictionaryItems()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增字典项
            dictApi.addDictItem(this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadDictionaryItems()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 删除字典项
    handleDelete(id) {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无删除字典项权限');
        return;
      }
      this.$confirm('确定要删除该字典项吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        dictApi.deleteDictItem(id).then(response => {
          if (response.success) {
            this.$message.success(response.message || '删除成功')
            this.loadDictionaryItems()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 批量删除字典项
    handleBatchDelete() {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无批量删除字典项权限');
        return;
      }
      if (this.selectedIds.length === 0) {
        this.$message.warning('请选择要删除的字典项');
        return;
      }
      this.$confirm('确定要批量删除选中的字典项吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        dictApi.deleteDictItemBatch(this.selectedIds).then(response => {
          if (response.success) {
            this.$message.success(response.message || '批量删除成功')
            this.loadDictionaryItems()
            this.selectedIds = []
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('批量删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },
  }
}
</script>

<style scoped>
.parent-info-card {
  margin-bottom: 20px;
  border-left: 3px solid #409EFF;
  /* 保持标识性边框 */
}

.dict-type-info {
  margin: 10px 0;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.info-label {
  width: 120px;
  /* 固定标签宽度，确保对齐 */
  text-align: right;
  margin-right: 10px;
}
</style>